import { useCurrentUserRole } from "Hooks/useCurrentUserAccessRole";
import { getApiUrl } from "Infrastructure/Api/Api";
import { type AccessRoleCode, AccessRoleCodes } from "Models/AccessRoleCodes";
import {
  Resources,
  type SocialTermsDocumentRole,
  useResource,
} from "Translations/Resources";

const RESOURCES = Resources.Social.Terms.Documents;

/** Hunter and HunterPlus share the business documents; roles outside the social surface have none. */
const ROLE_TO_DOCUMENT_ROLE: Partial<
  Record<AccessRoleCode, SocialTermsDocumentRole>
> = {
  [AccessRoleCodes.Customer]: "Customer",
  [AccessRoleCodes.Merchant]: "Merchant",
  [AccessRoleCodes.Hunter]: "Hunter",
  [AccessRoleCodes.HunterPlus]: "Hunter",
};

/**
 * Documents of the active persona's Chat contract with Mo.one Digital Services s.r.o. (never
 * Mo.one a.s., which only provides the payments). Each document has a URL per role family —
 * users (Customer) and businesses (Merchant / Hunter) get different VOP, chat terms and
 * complaint rules; the rest is shared today but may diverge. Served by the API, one path per
 * language. `terms`, `chatTerms` and `communityRules` are the three the consent checkbox names;
 * `all` is the download list of the "Shrnutí a dokumenty" block in its contractual order.
 * Deliberately absent from the legal-documents pages: a separate contract with a different
 * provider, kept apart from the Mo.one a.s. documents.
 */
export const useSocialTermsDocuments = () => {
  const { t } = useResource();
  const role = useCurrentUserRole();
  // Only roles with a social surface ever render the consent block (SocialConstants.DiscoverableRoles),
  // so the fallback is theoretical — it keeps the documents non-nullable for the callers.
  const documentRole: SocialTermsDocumentRole =
    (role && ROLE_TO_DOCUMENT_ROLE[role]) || "Customer";
  const apiUrl = getApiUrl();
  const getDocument = (resource: {
    Title: string;
    Url: Record<SocialTermsDocumentRole, string>;
  }) => ({
    title: t(resource.Title),
    url: `${apiUrl}${t(resource.Url[documentRole])}`,
  });

  const contractSummary = getDocument(RESOURCES.ContractSummary);
  const terms = getDocument(RESOURCES.Terms);
  const chatTerms = getDocument(RESOURCES.ChatTerms);
  const communityRules = getDocument(RESOURCES.CommunityRules);
  const complaintRules = getDocument(RESOURCES.ComplaintRules);
  const technicalTerms = getDocument(RESOURCES.TechnicalTerms);
  const privacyPolicy = getDocument(RESOURCES.PrivacyPolicy);

  return {
    terms,
    chatTerms,
    communityRules,
    privacyPolicy,
    all: [
      contractSummary,
      terms,
      chatTerms,
      communityRules,
      complaintRules,
      technicalTerms,
      privacyPolicy,
    ],
  };
};
